import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertTriangle, ChevronRight, Home, Lock } from 'lucide-react';
import { dashboardApi } from '../../services/dashboard-api.service';
import type {
  TerritorialAbstract as TerritorialAbstractType,
  TerritorialAbstractChild,
  TerritorialLevel,
  WorkloadSummary,
  PendingTaskGroup,
} from '../../types/dashboard.types';

const GRANT_OPTIONS = ['All', 'CRR', 'MRR', 'SDF', 'FDR', 'PMGSY', 'MGNREGS', 'DMFT', 'IMS'];

// Human label for the level whose children are currently listed.
const LEVEL_LABEL: Record<TerritorialLevel, string> = {
  territory: 'Territories',
  circle: 'Circles',
  division: 'Divisions',
  subdivision: 'Sub-divisions',
  mandal: 'Mandals',
  work: 'Works',
};

// One hop in the drill-down trail. `childLevel` is what gets listed when this
// node is the active scope; `parentId` is the node being drilled into.
interface PathNode {
  name: string;
  childLevel: TerritorialLevel;
  parentId?: number;
}

const ROOT: PathNode = { name: 'All Territories', childLevel: 'territory', parentId: undefined };

// ==========================================
// ABSTRACT STATUS TABLE (matches DashboardAbstract Section 1)
// ==========================================

const AbstractStatusTable: React.FC<{ ws?: WorkloadSummary; asOnDate: string }> = ({ ws, asOnDate }) => (
  <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
    <div className="bg-blue-700 text-white px-6 py-3">
      <h2 className="text-base font-bold">ABSTRACT STATUS OF PRED WORKS DURING THIS FINANCIAL YEAR AS ON {asOnDate}</h2>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-100">
            <th colSpan={3} className="px-3 py-2 text-center border font-semibold text-blue-800">Total work load<br /><span className="text-xs font-normal">(Spill over + New sanctions)</span></th>
            <th colSpan={3} className="px-3 py-2 text-center border font-semibold text-green-800">Completed</th>
            <th colSpan={3} className="px-3 py-2 text-center border font-semibold text-orange-800">Progress</th>
            <th colSpan={3} className="px-3 py-2 text-center border font-semibold text-red-800">Not Started</th>
          </tr>
          <tr className="bg-gray-50 text-xs text-gray-600">
            {['Nos', 'Length', 'Est', 'Nos', 'Length', 'Est', 'Nos', 'Length', 'Est', 'Nos', 'Length', 'Est'].map((h, i) => (
              <th key={i} className="px-3 py-1.5 border text-center">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="text-center font-bold text-lg">
            <td className="px-3 py-3 border text-blue-700">{ws?.total.count || 0}</td>
            <td className="px-3 py-3 border text-blue-700">{(ws?.total.length_km || 0).toFixed(1)}</td>
            <td className="px-3 py-3 border text-blue-700">{(ws?.total.estimate_cost_lakhs || 0).toFixed(2)}</td>
            <td className="px-3 py-3 border text-green-700">{ws?.completed.count || 0}</td>
            <td className="px-3 py-3 border text-green-700">{(ws?.completed.length_km || 0).toFixed(1)}</td>
            <td className="px-3 py-3 border text-green-700">{(ws?.completed.estimate_cost_lakhs || 0).toFixed(2)}</td>
            <td className="px-3 py-3 border text-orange-700">{ws?.in_progress.count || 0}</td>
            <td className="px-3 py-3 border text-orange-700">{(ws?.in_progress.length_km || 0).toFixed(1)}</td>
            <td className="px-3 py-3 border text-orange-700">{(ws?.in_progress.estimate_cost_lakhs || 0).toFixed(2)}</td>
            <td className="px-3 py-3 border text-red-700">{ws?.not_started.count || 0}</td>
            <td className="px-3 py-3 border text-red-700">{(ws?.not_started.length_km || 0).toFixed(1)}</td>
            <td className="px-3 py-3 border text-red-700">{(ws?.not_started.estimate_cost_lakhs || 0).toFixed(2)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
);

// ==========================================
// CHILD BREAKDOWN TABLE (one row per child node, click to drill)
// ==========================================

const ChildBreakdownTable: React.FC<{
  childLevel: TerritorialLevel;
  children: TerritorialAbstractChild[];
  onDrill: (c: TerritorialAbstractChild) => void;
}> = ({ childLevel, children, onDrill }) => {
  const sum = (pick: (c: TerritorialAbstractChild) => number) => children.reduce((s, c) => s + pick(c), 0);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="bg-gray-700 text-white px-6 py-2.5">
        <h3 className="text-sm font-bold">{LEVEL_LABEL[childLevel]} &mdash; click a row to drill down</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th rowSpan={2} className="px-3 py-2 border text-left font-semibold w-12">Sno</th>
              <th rowSpan={2} className="px-3 py-2 border text-left font-semibold">{LEVEL_LABEL[childLevel].replace(/s$/, '')}</th>
              <th colSpan={3} className="px-3 py-1.5 border text-center font-semibold text-blue-800">Total</th>
              <th colSpan={3} className="px-3 py-1.5 border text-center font-semibold text-green-800">Completed</th>
              <th colSpan={3} className="px-3 py-1.5 border text-center font-semibold text-orange-800">Progress</th>
              <th colSpan={3} className="px-3 py-1.5 border text-center font-semibold text-red-800">Not Started</th>
              <th rowSpan={2} className="px-2 py-2 border w-8"></th>
            </tr>
            <tr className="bg-gray-50 text-xs text-gray-600">
              {['Nos', 'Len', 'Est', 'Nos', 'Len', 'Est', 'Nos', 'Len', 'Est', 'Nos', 'Len', 'Est'].map((h, i) => (
                <th key={i} className="px-3 py-1 border text-center">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {children.map((c, i) => (
              <tr key={`${c.level}-${c.id}`} className="text-center hover:bg-blue-50 cursor-pointer" onClick={() => onDrill(c)}>
                <td className="px-3 py-2 border">{i + 1}</td>
                <td className="px-3 py-2 border text-left font-medium text-blue-700">{c.name}</td>
                <td className="px-3 py-2 border">{c.workload.total.count}</td>
                <td className="px-3 py-2 border">{c.workload.total.length_km.toFixed(1)}</td>
                <td className="px-3 py-2 border">{c.workload.total.estimate_cost_lakhs.toFixed(2)}</td>
                <td className="px-3 py-2 border text-green-700">{c.workload.completed.count}</td>
                <td className="px-3 py-2 border text-green-700">{c.workload.completed.length_km.toFixed(1)}</td>
                <td className="px-3 py-2 border text-green-700">{c.workload.completed.estimate_cost_lakhs.toFixed(2)}</td>
                <td className="px-3 py-2 border text-orange-700">{c.workload.in_progress.count}</td>
                <td className="px-3 py-2 border text-orange-700">{c.workload.in_progress.length_km.toFixed(1)}</td>
                <td className="px-3 py-2 border text-orange-700">{c.workload.in_progress.estimate_cost_lakhs.toFixed(2)}</td>
                <td className="px-3 py-2 border text-red-700">{c.workload.not_started.count}</td>
                <td className="px-3 py-2 border text-red-700">{c.workload.not_started.length_km.toFixed(1)}</td>
                <td className="px-3 py-2 border text-red-700">{c.workload.not_started.estimate_cost_lakhs.toFixed(2)}</td>
                <td className="px-2 py-2 border text-gray-400"><ChevronRight className="h-4 w-4" /></td>
              </tr>
            ))}
            {children.length === 0 && (
              <tr><td colSpan={15} className="px-4 py-8 text-center text-gray-400">No {LEVEL_LABEL[childLevel].toLowerCase()} found</td></tr>
            )}
            {children.length > 0 && (
              <tr className="text-center font-bold bg-gray-50 border-t-2 border-gray-300">
                <td className="px-3 py-2 border" colSpan={2}>Total</td>
                <td className="px-3 py-2 border">{sum(c => c.workload.total.count)}</td>
                <td className="px-3 py-2 border">{sum(c => c.workload.total.length_km).toFixed(1)}</td>
                <td className="px-3 py-2 border">{sum(c => c.workload.total.estimate_cost_lakhs).toFixed(2)}</td>
                <td className="px-3 py-2 border text-green-700">{sum(c => c.workload.completed.count)}</td>
                <td className="px-3 py-2 border text-green-700">{sum(c => c.workload.completed.length_km).toFixed(1)}</td>
                <td className="px-3 py-2 border text-green-700">{sum(c => c.workload.completed.estimate_cost_lakhs).toFixed(2)}</td>
                <td className="px-3 py-2 border text-orange-700">{sum(c => c.workload.in_progress.count)}</td>
                <td className="px-3 py-2 border text-orange-700">{sum(c => c.workload.in_progress.length_km).toFixed(1)}</td>
                <td className="px-3 py-2 border text-orange-700">{sum(c => c.workload.in_progress.estimate_cost_lakhs).toFixed(2)}</td>
                <td className="px-3 py-2 border text-red-700">{sum(c => c.workload.not_started.count)}</td>
                <td className="px-3 py-2 border text-red-700">{sum(c => c.workload.not_started.length_km).toFixed(1)}</td>
                <td className="px-3 py-2 border text-red-700">{sum(c => c.workload.not_started.estimate_cost_lakhs).toFixed(2)}</td>
                <td className="px-2 py-2 border"></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ==========================================
// WORKS LEAF TABLE (mandal -> individual works)
// ==========================================

const WorksLeafTable: React.FC<{ groups: PendingTaskGroup[] }> = ({ groups }) => {
  const works = groups.flatMap(g => g.works);
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="bg-gray-700 text-white px-6 py-2.5">
        <h3 className="text-sm font-bold">Works ({works.length})</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50">
            <tr>
              {['Sno', 'Work ID', 'Name of the work', 'Grant', 'Type', 'Estimate Cost', 'Sanctioned Length', 'Stage of work'].map(h => (
                <th key={h} className="px-2 py-2 text-left font-semibold text-gray-700 whitespace-nowrap border-b border-gray-300">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {works.map((w, idx) => (
              <tr key={`${w.work_id}-${idx}`} className="hover:bg-blue-50">
                <td className="px-2 py-1.5">{idx + 1}</td>
                <td className="px-2 py-1.5">{w.work_id}</td>
                <td className="px-2 py-1.5"><span className="max-w-[260px] block truncate" title={w.work_name}>{w.work_name}</span></td>
                <td className="px-2 py-1.5">{w.grant_type}</td>
                <td className="px-2 py-1.5">{w.work_type || '-'}</td>
                <td className="px-2 py-1.5">{w.estimate_cost.toFixed(2)}</td>
                <td className="px-2 py-1.5">{w.length_sanctioned_km}</td>
                <td className="px-2 py-1.5">{w.sub_stage_of_work || w.abstract_stage_of_work || '-'}</td>
              </tr>
            ))}
            {works.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No works found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ==========================================
// MAIN COMPONENT
// ==========================================

const TerritorialAbstract: React.FC = () => {
  const [path, setPath] = useState<PathNode[]>([ROOT]);
  const [grant, setGrant] = useState('All');
  const [data, setData] = useState<TerritorialAbstractType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  const [scopeName, setScopeName] = useState('');

  // Lock the drill-down to the user's jurisdiction. When their level has an engineering child
  // node (territory/circle/division/sub-division/mandal) start the breadcrumb AT that node so
  // they cannot navigate above it. A DISTRICT scope has no node in this engineering hierarchy,
  // so we keep the root but still badge it — the backend scope-enforces the data to their district.
  useEffect(() => {
    dashboardApi.getMyScope().then((s: any) => {
      if (!s || s.isState || !s.scopeId) return;
      const childOf: Record<string, string> = { TERRITORY: 'circle', CIRCLE: 'division', DIVISION: 'subdivision', SUB_DIVISION: 'mandal', MANDAL: 'work' };
      setLocked(true);
      setScopeName(`${s.name || s.level} (${String(s.level).replace('_', ' ').toLowerCase()})`);
      if (childOf[s.level]) setPath([{ name: s.name || s.level, childLevel: childOf[s.level] as any, parentId: s.scopeId }]);
    }).catch(() => {});
  }, []);

  const current = path[path.length - 1];

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const g = grant === 'All' ? undefined : grant;
      const result = await dashboardApi.getTerritorialAbstract(current.childLevel, current.parentId, g);
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load drill-down');
    } finally {
      setLoading(false);
    }
  }, [current.childLevel, current.parentId, grant]);

  useEffect(() => { loadData(); }, [loadData]);

  const drillInto = (c: TerritorialAbstractChild) => {
    setPath(prev => [...prev, { name: c.name, childLevel: c.next_level, parentId: c.id }]);
  };

  const goToCrumb = (idx: number) => {
    setPath(prev => prev.slice(0, idx + 1));
  };

  const asOnDate = data?.as_on_date || new Date().toISOString().split('T')[0];

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-lg font-bold text-gray-800">Abstract Status &mdash; Territorial Drill-down</h1>
          <p className="text-xs text-gray-500">Territory &rarr; Circle &rarr; Division &rarr; Sub-division &rarr; Mandal &rarr; Works</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Grant</label>
          <select
            value={grant}
            onChange={e => setGrant(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white"
          >
            {GRANT_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <button onClick={loadData} className="flex items-center gap-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white hover:bg-gray-50">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Breadcrumb */}
      <nav className="flex flex-wrap items-center gap-1 mb-4 text-sm">
        {locked && (
          <span className="inline-flex items-center gap-1 mr-1 text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-0.5"
            title="Data is limited to your assigned jurisdiction">
            <Lock className="h-3 w-3" /> {scopeName || 'your scope'}
          </span>
        )}
        {path.map((p, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && <ChevronRight className="h-4 w-4 text-gray-400" />}
            <button
              onClick={() => goToCrumb(idx)}
              className={`flex items-center gap-1 px-2 py-1 rounded ${idx === path.length - 1 ? 'font-semibold text-blue-700 bg-blue-50' : 'text-blue-600 hover:underline'}`}
            >
              {idx === 0 && <Home className="h-3.5 w-3.5" />}
              {p.name}
            </button>
          </React.Fragment>
        ))}
      </nav>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-center justify-between">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
            <span className="text-red-800 text-sm">{error}</span>
          </div>
          <button onClick={loadData} className="text-red-600 hover:text-red-800 underline text-sm">Retry</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-500 mr-3" />
          <span className="text-gray-600">Loading drill-down data...</span>
        </div>
      ) : data && (
        <div className="space-y-6">
          <AbstractStatusTable ws={data.scope_workload} asOnDate={asOnDate} />
          {current.childLevel === 'work'
            ? <WorksLeafTable groups={data.works} />
            : <ChildBreakdownTable childLevel={data.level || current.childLevel} children={data.children} onDrill={drillInto} />}
        </div>
      )}
    </div>
  );
};

export default TerritorialAbstract;
